import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import {
  useCreateProgram, useUpdateProgram, useMarkets,
  useProducts, usePackages, useFAQs, useAssets,
} from '@/hooks/usePartnerships';
import { MultiEntityPicker } from './MultiEntityPicker';
import type { Program } from '../../../../shared/types/partnerships';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  program?: Program;
}

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;


interface FormData {
  name: string; description: string; objective: string;
  eligibilityRequirements: string; visualUrls: string;
  driveUrl: string; notes: string; status: string;
}

export function ProgramFormDialog({ open, onOpenChange, partnerId, program }: Props) {
  const { t } = useTranslation();
  const isEditing = !!program;
  const create = useCreateProgram();
  const update = useUpdateProgram();
  const { data: markets = [] } = useMarkets();
  const { data: products = [] } = useProducts(partnerId);
  const { data: packages = [] } = usePackages(partnerId);
  const { data: faqs = [] } = useFAQs(partnerId);
  const { data: assets = [] } = useAssets(partnerId);

  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [selectedFaqIds, setSelectedFaqIds] = useState<string[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');

  useEffect(() => {
    if (open) {
      setSelectedMarkets(program?.availableMarkets || []);
      setSelectedProductIds(program?.includedProductIds || []);
      setSelectedPackageIds(program?.includedPackageIds || []);
      setSelectedFaqIds(program?.relatedFaqIds || []);
      setSelectedAssetIds(program?.relatedAssetIds || []);
      reset({
        name: program?.name || '', description: program?.description || '',
        objective: program?.objective || '',
        eligibilityRequirements: program?.eligibilityRequirements || '',
        visualUrls: toLines(program?.visualUrls),
        driveUrl: program?.driveUrl || '', notes: program?.notes || '',
        status: program?.status || 'PUBLISHED',
      });
    }
  }, [open, program, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name, description: data.description, objective: data.objective,
      availableMarkets: selectedMarkets,
      eligibilityRequirements: data.eligibilityRequirements || undefined,
      includedProductIds: selectedProductIds.length ? selectedProductIds : undefined,
      includedPackageIds: selectedPackageIds.length ? selectedPackageIds : undefined,
      relatedFaqIds: selectedFaqIds.length ? selectedFaqIds : undefined,
      relatedAssetIds: selectedAssetIds.length ? selectedAssetIds : undefined,
      visualUrls: data.visualUrls ? fromLines(data.visualUrls) : undefined,
      driveUrl: data.driveUrl || undefined, notes: data.notes || undefined,
      status: data.status as any,
    };

    if (isEditing) {
      await update.mutateAsync({ programId: program!.programId, ...payload, expectedUpdatedAt: program!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;
  const activeMarkets = markets.filter(m => m.active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.program_form.title_edit') : t('partnerships.program_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.name_required')}</Label>
            <Input {...register('name', { required: true })} placeholder={t('partnerships.program_form.youtube_brand_deals_q4_2025')} />
            {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.description_label')}</Label>
            <Textarea {...register('description')} rows={3} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.program_form.objective_label')}</Label>
            <Textarea {...register('objective')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.available_markets_label')}</Label>
            <div className="flex flex-wrap gap-1.5 p-2 border rounded-md min-h-10">
              {activeMarkets.map(m => (
                <button
                  key={m.marketId}
                  type="button"
                  onClick={() => setSelectedMarkets(prev =>
                    prev.includes(m.name) ? prev.filter(n => n !== m.name) : [...prev, m.name]
                  )}
                  className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                    selectedMarkets.includes(m.name)
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-muted-foreground/30 hover:border-foreground/50'
                  }`}
                >
                  {m.name}
                </button>
              ))}
              {activeMarkets.length === 0 && (
                <p className="text-xs text-muted-foreground self-center">{t('partnerships.common.no_markets_configured')}</p>
              )}
            </div>
            {selectedMarkets.length > 0 && (
              <p className="text-xs text-muted-foreground">{t('partnerships.common.selected_count', { count: selectedMarkets.length })}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.program_form.eligibility_label')}</Label>
            <Textarea {...register('eligibilityRequirements')} rows={2} />
          </div>

          <MultiEntityPicker
            label={t('partnerships.program_form.included_products_label')}
            items={products.map(p => ({ id: p.productId, label: p.name, sub: p.category }))}
            selected={selectedProductIds}
            onChange={setSelectedProductIds}
            placeholder={t('partnerships.program_form.product_search_placeholder')}
          />

          <MultiEntityPicker
            label={t('partnerships.program_form.included_packages_label')}
            items={packages.map(p => ({ id: p.packageId, label: p.name }))}
            selected={selectedPackageIds}
            onChange={setSelectedPackageIds}
            placeholder={t('partnerships.program_form.package_search_placeholder')}
          />

          <MultiEntityPicker
            label={t('partnerships.program_form.related_faqs_label')}
            items={faqs.map(f => ({ id: f.faqId, label: f.question, sub: f.category }))}
            selected={selectedFaqIds}
            onChange={setSelectedFaqIds}
            placeholder={t('partnerships.program_form.faq_search_placeholder')}
          />

          <MultiEntityPicker
            label={t('partnerships.program_form.related_assets_label')}
            items={assets.map(a => ({ id: a.assetId, label: a.name, sub: a.assetType }))}
            selected={selectedAssetIds}
            onChange={setSelectedAssetIds}
            placeholder={t('partnerships.program_form.asset_search_placeholder')}
          />

          <div className="space-y-1.5">
            <Label>{t('partnerships.program_form.visual_urls_label')}</Label>
            <Textarea {...register('visualUrls')} rows={2} placeholder="https://..." />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.drive_link_optional_label')}</Label>
            <Input {...register('driveUrl')} type="url" placeholder="https://drive.google.com/..." />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.internal_notes_optional_label')}</Label>
            <Textarea {...register('notes')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.program_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
